import SearchPanel from '@components/searchPanel'
import { Grid, IconButton, Tooltip } from '@mui/material'
import * as common from '@common/common'
import { GlobalContext } from '@providers/globalStore'
import { InitialSearchPanelState, SearchPanelContext } from '@providers/SearchPanelProvider'
import SearchPanelReducer, { SearchPanelState } from '@reducers/searchPanelReducer'
import moment from 'moment'
import { Fragment, useContext, useReducer, useRef, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'
import useTableView from '@hooks/useTableView'
import { useTranslation } from 'react-i18next'
import FormStatusLabel from '@components/FormStatusLabel'
import GetValueWithKey from '@utils/getValueWithKey'
import { FormHwrListModel, HwrFilterOptions } from '@services/model/form/form.HWR.model'
import { NavigateTo } from '@utils/navigate'
import FormHWRService from '@services/formService/form.HWR.service'
import { RightsCategory } from '@services/model/form/form.model'
import ExportIcon from '@components/icon/export'
import { exportToCSV } from '@utils/exportExcel'

const AllRecord = (props: { showMyRecord?: boolean }) => {
  const refMounted = useRef(false)
  const { state: globalState, userInfo } = useContext(GlobalContext)
  const [state, dispatch] = useReducer<React.Reducer<SearchPanelState, any>>(
    SearchPanelReducer,
    InitialSearchPanelState,
  )

  const { t } = useTranslation()
  const [extraListInfo, setExtraListInfo] = useState<HwrFilterOptions>({
    submittedByList: [],
    approvedByList: [],
    formIdList: [],
    workOrderIdList: [],
  })

  const getExtraListInfo = async () => {
    let extraInfo = await FormHWRService.GetHwrFilterOption(props.showMyRecord)
    setExtraListInfo(extraInfo)
    refMounted.current = true
  }

  const getTableFullList = async () => {
    let fullList    = await FormHWRService.GetHwrList(
        {
          pagination: { skipPagination: true },
        },
        '',
        props.showMyRecord || false,
    )

    let list: any[] = fullList.list.map((x) => {
      return {
        'General Status'   : x.formStatusShortName,
        'Submission Status': x.formStatus,
        'Form ID'          : x.reportNo,
        'Contract No'      : x.contractNo,
        'Work Order No'    : x.workOrderNo,
        'Submitted By'     : GetValueWithKey(x.submittedBy, globalState.userMetaList ?? []),
        'Approved By'      : GetValueWithKey(x.approvedBy, globalState.userMetaList ?? []),
        'Last update'      : x.updatedAt ? moment(x.updatedAt).format(moment.defaultFormat) : 'N/A',
      }
    })
    return list ?? []
  }

  const [TableView, reload] = useTableView<FormHwrListModel>({
    headers: [
      {
        key: 'formStatusShortName',
        desc: t('General Status', { ns: common.i18nFormNS }),
        renderCell: (record: FormHwrListModel) => (
          <Fragment>
            <FormStatusLabel
              label={record.formStatusShortName}
              color={record.formStatusColor || ''}
            />
          </Fragment>
        ),
      },
      {
        key: 'formStatus',
        desc: t('Submission Status', { ns: common.i18nFormNS }),
        renderCell: (record: FormHwrListModel) => <Fragment>{record.formStatus}</Fragment>,
      },
      {
        key: 'formId',
        desc: t('Form ID', { ns: common.i18nFormNS }),
        renderCell: (record: FormHwrListModel) => <Fragment>{record.reportNo}</Fragment>,
      },
      {
        key: 'contractNo',
        desc: t('Contract No', { ns: common.i18nFormNS }),
        renderCell: (record: FormHwrListModel) => <Fragment>{record.contractNo}</Fragment>,
      },
      {
        key: 'workOrderId',
        desc: t('Work Order No', { ns: common.i18nFormNS }),
        renderCell: (record: FormHwrListModel) => <Fragment>{record.workOrderNo}</Fragment>,
      },
      {
        key: 'submittedBy',
        desc: t('Submitted By', { ns: common.i18nFormNS }),
        renderCell: (record: FormHwrListModel) => (
          <Fragment>{GetValueWithKey(record.submittedBy, globalState.userMetaList ?? [])}</Fragment>
        ),
        mobileHidden: true,
      },
      {
        key: 'approvedBy',
        desc: t('Approved By', { ns: common.i18nFormNS }),
        renderCell: (record: FormHwrListModel) => (
          <Fragment>{GetValueWithKey(record.approvedBy, globalState.userMetaList ?? [])}</Fragment>
        ),
        mobileHidden: true,
      },
      {
        key: 'updatedAt',
        desc: t('Last Update', { ns: common.i18nFormNS }),
        renderCell: (record: FormHwrListModel) => (
          <Fragment>
            {record.updatedAt ? moment(record.updatedAt).format(moment.defaultFormat) : ''}
          </Fragment>
        ),
        mobileHidden: true,
      },
    ],

    onRowClick: function (navigate: NavigateFunction, record: FormHwrListModel) {
      NavigateTo(navigate, '/hidden-works-record/:id', { id: record.formId })
    },
    mountedRef: refMounted,
    reloadCallback: async (pagination: object, sorting: object | undefined, cancelToken: any) => {
      if (refMounted.current) {
        sessionStorage.setItem('HWR', JSON.stringify(state.filterResult))
        let resp = await FormHWRService.GetHwrList(
          {
            ...state.filterResult,
            sorting: sorting,
            pagination: pagination,
          },
          cancelToken,
          props.showMyRecord || false,
        )
        return resp
      }
    },
  })

  return (
    <Grid component="main" container padding={1}>
      <SearchPanelContext.Provider value={{ state, dispatch }}>
        <SearchPanel
          dispatch={dispatch}
          addUrl="/booking/new"
          onSearch={reload}
          onInitReload={reload}
          onToggleFilterDrawer={getExtraListInfo}
          mountedRef={refMounted}
          excludeStatus={true}
          criteria={[
            {
              key: 'formStatus',
              desc: 'Submission Status',
              type: 'NumberSelectionFilter',
              multiple: true,
              valueList:
                globalState.formStatusList
                  ?.filter((x) => x.actionCode.includes(RightsCategory.FORM_HWR))
                  .map((x) => {
                    return {
                      key: x.id,
                      value: x.actionName,
                    }
                  }) || [],
              defaultValue:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.formStatus?.value
                  : [],
            },
            {
              key: 'formId',
              desc: 'Form ID',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: extraListInfo.formIdList,
              defaultValue:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.formId?.value
                  : [],
            },
            {
              key: 'contractNo',
              desc: 'Contract No.',
              type: 'NumberSelectionFilter',
              multiple: true,
              valueList: globalState.contractList?.map((x) => {
                return {
                  key: x.id,
                  value: x.contractNo,
                }
              }),
              defaultValue:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.contractNo?.value
                  : [],
            },
            {
              key: 'workOrderId',
              desc: 'Works Order No',
              type: 'StringSelectionFilter',
              multiple: true,
               valueList: globalState.worksOrderList?.filter((x) =>
                extraListInfo.workOrderIdList?.includes(x.id),
              )?.map(({ id, workOrderNo}) => ({ key: id, value: workOrderNo})),
              defaultValue:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.workOrderId?.value
                  : [],
            },
            {
              key: 'submittedBy',
              desc: 'Submitted By',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: globalState.userMetaList?.filter((x) =>
                extraListInfo.submittedByList?.includes(x.key),
              ),
              defaultValue:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.submittedBy?.value
                  : [],
            },
            {
              key: 'approvedBy',
              desc: 'Approved By',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: globalState.userMetaList?.filter((x) =>
                extraListInfo.approvedByList?.includes(x.key),
              ),
              defaultValue:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.approvedBy?.value
                  : [],
            },
            {
              key: 'updatedAt',
              desc: 'Last Update',
              type: 'DateFilter',
              defaultOperator:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.updatedAt?.operator
                  : 'in',
              defaultFrom:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.updatedAt?.value?.min
                  : '',
              defaultTo:
                sessionStorage?.HWR !== 'undefined' && sessionStorage?.HWR !== undefined
                  ? JSON.parse(sessionStorage?.HWR)?.updatedAt?.value?.max
                  : '',
            },
          ]}
          extraButtons={
            <Tooltip title="Export">
              <IconButton
                sx={{
                  backgroundColor: globalState.headerColor,
                  borderRadius: '10%',
                  width: '40px',
                  height: '40px',
                  '&:hover': { background: globalState.headerColor },
                }}
                onClick={async () => await exportToCSV(await getTableFullList(), 'HWR-all-record')}>
                <ExportIcon />
              </IconButton>
            </Tooltip>
          }
        />
        <TableView />
      </SearchPanelContext.Provider>
    </Grid>
  )
}

export default AllRecord
